import React from "react";

// Official-style state security instruments (body only — each binder wraps with its own <Esign>).
// Supported here: FL (Mortgage), CA (Deed of Trust). GA is rendered inline in the binders.
export const SHARED_DEED_STATES = ["FL", "CA"];

const LENDER = "SEUTRADE FINANCIAL SERVICES";
const LENDER_ADDR = "213 N Market Street #1011, Wilmington, DE 19801";

const H2 = ({ children }) => (
  <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", marginTop: 16 }}>{children}</h2>
);
const Blank = ({ w = 200 }) => (
  <span style={{ display: "inline-block", borderBottom: "1px solid #6b6250", minWidth: w }}>&nbsp;</span>
);

function deedVals(d, fmtMoney) {
  const cd = d.closingDate ? new Date(d.closingDate) : null;
  const m = d.noteMaturityMonths || d.newTermMonths || 360;
  const maturityStr = cd && !isNaN(cd.getTime())
    ? new Date(cd.getFullYear(), cd.getMonth() + m, cd.getDate()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "________________, 20___";
  const p = Number(d.payoffAmount || 0);
  return {
    borrower: d.borrowerName || "____________________",
    principal: fmtMoney(d.payoffAmount),
    county: (d.propertyCounty || "").replace(/county/i, "").trim() || "____________",
    legal: d.legalDescription || 'See Exhibit "A" attached hereto and incorporated herein by reference.',
    parcel: d.taxParcelId || "____________________",
    address: d.collateral || "____________________",
    maturityStr,
    docStamp: (Math.ceil(p / 100) * 0.35).toFixed(2),
    intangible: (p * 0.002).toFixed(2),
  };
}

function FloridaMortgage({ d, fmtMoney }) {
  const v = deedVals(d, fmtMoney);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, fontSize: 11.5, lineHeight: 1.55, marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700 }}>This Instrument Prepared By:</div>
          <div>Susan Hill</div><div>{LENDER}</div><div>{LENDER_ADDR}</div>
        </div>
        <div>
          <div style={{ fontWeight: 700 }}>RECORD AND RETURN TO:</div>
          <div>{LENDER}</div><div>{LENDER_ADDR}</div>
          <div style={{ marginTop: 6 }}>PARCEL ID / FOLIO NO.: {v.parcel}</div>
        </div>
      </div>
      <h1 className="lg-title" style={{ textAlign: "center" }}>MORTGAGE</h1>
      <div className="lg-rule" />
      <p>THIS MORTGAGE (the <em>"Mortgage"</em>) is made and executed this _____ day of _______________, 20___, by and between <strong>{v.borrower}</strong> (hereinafter the <em>"Mortgagor"</em>), and <strong>{LENDER}</strong>, whose mailing address is {LENDER_ADDR} (hereinafter the <em>"Mortgagee"</em>).</p>

      <H2>STATUTORY TAX DATA (Ch. 201, Florida Statutes)</H2>
      <ul className="lg-ul" style={{ fontSize: 12, lineHeight: 1.7 }}>
        <li>Original Principal Indebtedness: <strong>{v.principal}</strong></li>
        <li>Maturity Date of Note: <strong>{v.maturityStr}</strong></li>
        <li>Florida Documentary Stamp Tax on the Note (§201.08, $0.35 per $100): <strong>${v.docStamp}</strong></li>
        <li>Nonrecurring Intangible Personal Property Tax on the Mortgage (§199.133, $0.002 per $1): <strong>${v.intangible}</strong></li>
      </ul>

      <H2>1. GRANTING / MORTGAGING CLAUSE</H2>
      <p>To secure the payment of a Promissory Note of even date herewith in the principal amount of <strong>{v.principal}</strong> (the <em>"Note"</em>), together with interest, if any, and all renewals, extensions, and future advances thereof, the Mortgagor does hereby <strong>mortgage, grant, bargain, sell, and convey</strong> to the Mortgagee, its successors and assigns, that certain real property lying and being in <strong>{v.county}</strong> County, Florida, more particularly described as follows:</p>
      <div style={{ border: "1px solid #d9cfa6", background: "#faf7ec", padding: "10px 12px", fontSize: 12, lineHeight: 1.6, margin: "8px 0" }}>{v.legal}</div>
      <p><strong>TOGETHER WITH</strong> all the improvements now or hereafter erected on the property, and all easements, appurtenances, rents, and fixtures now or hereafter a part of the property (collectively, the <em>"Property"</em>).</p>

      <H2>2. COVENANTS AND AGREEMENTS</H2>
      <ul className="lg-ul" style={{ fontSize: 12, lineHeight: 1.7 }}>
        <li><strong>Payment of Debt:</strong> Mortgagor shall pay when due the principal of, and interest (if any) on, the debt evidenced by the Note and any late charges.</li>
        <li><strong>Taxes &amp; Liens:</strong> Mortgagor shall pay all taxes, assessments, and charges that may attain priority over this Mortgage before delinquency.</li>
        <li><strong>Insurance:</strong> Mortgagor shall keep the improvements insured against loss by fire and hazards within "extended coverage," naming Mortgagee as loss payee.</li>
        <li><strong>Preservation of Property:</strong> Mortgagor shall keep the Property in good repair and shall not commit waste.</li>
        <li><strong>Due on Sale:</strong> If all or any part of the Property is sold or transferred without Mortgagee's prior written consent, Mortgagee may require immediate payment in full.</li>
        <li><strong>Acceleration &amp; Judicial Foreclosure:</strong> Upon Mortgagor's default, Mortgagee may declare all sums secured immediately due and payable and may <strong>foreclose this Mortgage by judicial proceeding</strong> pursuant to Chapter 702, Florida Statutes, and shall be entitled to collect all costs of foreclosure and reasonable attorneys' fees.</li>
      </ul>

      <H2>3. EXECUTION, WITNESSES &amp; ACKNOWLEDGMENT</H2>
      <p>IN WITNESS WHEREOF, the Mortgagor has executed this Mortgage on the day and year first above written.</p>
      <p style={{ fontWeight: 700 }}>Signed, sealed and delivered in the presence of:</p>
      <div style={{ fontSize: 12, lineHeight: 2.2, marginTop: 10 }}>
        <div>_____________________________________ (SEAL)</div>
        <div>Mortgagor Signature &nbsp;&nbsp; Print Name: {v.borrower}</div>
        <div style={{ marginTop: 12, display: "flex", gap: 40, flexWrap: "wrap" }}>
          <div>_______________________________<br />Witness #1 — Print Name: <Blank w={140} /></div>
          <div>_______________________________<br />Witness #2 — Print Name: <Blank w={140} /></div>
        </div>
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.9, marginTop: 16 }}>
        <div style={{ fontWeight: 700 }}>STATE OF FLORIDA, COUNTY OF ___________________</div>
        <p>The foregoing instrument was acknowledged before me by means of ☐ physical presence or ☐ online notarization, this _____ day of _______________, 20___, by <strong>{v.borrower}</strong>, who is personally known to me or who has produced ______________________ as identification.</p>
        <div style={{ marginTop: 12 }}>_____________________________________</div>
        <div>Notary Public, State of Florida</div>
        <div>My Commission Expires: _______________</div>
        <div style={{ color: "#8a7f55", marginTop: 6 }}>[Affix Notary Seal/Stamp Here]</div>
      </div>
    </>
  );
}

function CaliforniaDeedOfTrust({ d, fmtMoney }) {
  const v = deedVals(d, fmtMoney);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, fontSize: 11.5, lineHeight: 1.55, marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Recording Requested By &amp; Prepared By:</div>
          <div>Susan Hill</div><div>{LENDER}</div><div>{LENDER_ADDR}</div>
        </div>
        <div>
          <div style={{ fontWeight: 700 }}>AND WHEN RECORDED MAIL TO:</div>
          <div>{LENDER}</div><div>{LENDER_ADDR}</div>
          <div style={{ marginTop: 6 }}>ASSESSOR'S PARCEL NO. (APN): {v.parcel}</div>
        </div>
      </div>
      <h1 className="lg-title" style={{ textAlign: "center" }}>DEED OF TRUST</h1>
      <div style={{ textAlign: "center", fontSize: 12, letterSpacing: "0.08em", marginTop: -6, marginBottom: 4, color: "#6b6250" }}>WITH ASSIGNMENT OF RENTS AND POWER OF SALE</div>
      <div className="lg-rule" />
      <p>THIS DEED OF TRUST (the <em>"Deed of Trust"</em>) is made this _____ day of _______________, 20___, among the <strong>Trustor</strong>, <strong>{v.borrower}</strong>; the <strong>Trustee</strong>, {LENDER} Title Services, a California title company; and the <strong>Beneficiary</strong>, <strong>{LENDER}</strong>, whose address is {LENDER_ADDR}.</p>

      <H2>1. GRANT IN TRUST</H2>
      <p>Trustor <strong>IRREVOCABLY GRANTS, TRANSFERS AND ASSIGNS TO TRUSTEE, IN TRUST, WITH POWER OF SALE</strong>, that property in <strong>{v.county}</strong> County, State of California, described as:</p>
      <div style={{ border: "1px solid #d9cfa6", background: "#faf7ec", padding: "10px 12px", fontSize: 12, lineHeight: 1.6, margin: "8px 0" }}>{v.legal}</div>
      <p><strong>TOGETHER WITH</strong> the rents, issues, and profits thereof, subject to the right of Beneficiary to collect and apply such rents.</p>

      <H2>2. FOR THE PURPOSE OF SECURING</H2>
      <p>(1) Payment of the indebtedness evidenced by one Promissory Note of even date herewith in the principal sum of <strong>{v.principal}</strong>, maturing on <strong>{v.maturityStr}</strong>, executed by Trustor and payable to Beneficiary; (2) performance of each agreement of Trustor herein; and (3) payment of such further sums as may hereafter be advanced.</p>

      <H2>3. TRUSTOR COVENANTS</H2>
      <ul className="lg-ul" style={{ fontSize: 12, lineHeight: 1.7 }}>
        <li>To keep the Property in good condition and repair; to pay when due all taxes, assessments, and liens.</li>
        <li>To provide and maintain fire and hazard insurance for the benefit of Beneficiary.</li>
        <li>To appear in and defend any action affecting the security hereof or the rights or powers of Beneficiary or Trustee.</li>
      </ul>

      <H2>4. POWER OF SALE (Non-Judicial Foreclosure)</H2>
      <p>Upon default by Trustor in payment or performance, Beneficiary may declare all sums secured immediately due and payable and may cause the Trustee to record a <strong>Notice of Default</strong>. After the statutory period (not less than three (3) months), and after giving <strong>Notice of Sale</strong> as required by California Civil Code §§ 2924–2924h, Trustee may sell the Property at public auction to the highest bidder for cash and deliver to the purchaser its deed conveying the Property, and shall apply the proceeds to costs, the indebtedness, and the remainder, if any, to the person legally entitled thereto.</p>

      <H2>5. REQUEST FOR NOTICE</H2>
      <p>The undersigned Trustor requests that a copy of any <strong>Notice of Default</strong> and any <strong>Notice of Sale</strong> hereunder be mailed to Trustor at the address set forth above.</p>

      <H2>6. EXECUTION &amp; ACKNOWLEDGMENT</H2>
      <div style={{ fontSize: 12, lineHeight: 2.2, marginTop: 10 }}>
        <div>_____________________________________</div>
        <div>Trustor Signature &nbsp;&nbsp; Print Name: {v.borrower}</div>
      </div>
      <div style={{ fontSize: 11, lineHeight: 1.6, marginTop: 14, border: "1px solid #b9a86a", padding: "8px 10px", background: "#fbf9f0" }}>
        A notary public or other officer completing this certificate verifies only the identity of the individual who signed the document to which this certificate is attached, and not the truthfulness, accuracy, or validity of that document. (Cal. Civil Code § 1189)
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.9, marginTop: 12 }}>
        <div style={{ fontWeight: 700 }}>STATE OF CALIFORNIA, COUNTY OF ___________________</div>
        <p>On _______________, 20___, before me, ______________________________, Notary Public, personally appeared <strong>{v.borrower}</strong>, who proved to me on the basis of satisfactory evidence to be the person whose name is subscribed to the within instrument and acknowledged to me that he/she executed the same in his/her authorized capacity. I certify under PENALTY OF PERJURY under the laws of the State of California that the foregoing paragraph is true and correct. WITNESS my hand and official seal.</p>
        <div style={{ marginTop: 12 }}>_____________________________________</div>
        <div>Notary Public, State of California</div>
        <div style={{ color: "#8a7f55", marginTop: 6 }}>[Affix Notary Seal/Stamp Here]</div>
      </div>
    </>
  );
}

export function StateDeedBody({ state, d, fmtMoney }) {
  const s = String(state || "").toUpperCase();
  if (s === "FL") return <FloridaMortgage d={d} fmtMoney={fmtMoney} />;
  if (s === "CA") return <CaliforniaDeedOfTrust d={d} fmtMoney={fmtMoney} />;
  return null;
}

export function stateDeedTitle(state) {
  const s = String(state || "").toUpperCase();
  if (s === "FL") return "Mortgage (Florida)";
  if (s === "CA") return "Deed of Trust with Power of Sale (California)";
  return "Security Instrument";
}
